import {
  Cell,
  CellTemplate,
  Compatible,
  getCharFromKeyCode,
  isAlphaNumericKey,
  isNavigationKey,
  keyCodes,
  Uncertain,
  UncertainCompatible,
} from "@silevis/reactgrid";
import { ReactNode } from "react";
import { Input, Popover } from "antd";
import { Styles } from "./Style";
import { CellCustomType } from "../../type";

export class LinkCellTemplate implements CellTemplate<CellCustomType> {
  getCompatibleCell(
    uncertainCell: Uncertain<CellCustomType>,
  ): Compatible<CellCustomType> {
    const uncertainCellAs = uncertainCell as CellCustomType;
    return {
      value: NaN,
      ...uncertainCellAs,
    };
  }

  getClassName(
    cell: Compatible<CellCustomType>,
    isInEditMode: boolean,
  ): string {
    return cell.className || "";
  }

  update(
    cell: Compatible<CellCustomType>,
    cellToMerge: UncertainCompatible<CellCustomType>,
  ): Compatible<CellCustomType> {
    return this.getCompatibleCell({ ...cell, text: cellToMerge.text });
  }

  handleKeyDown(
    cell: Compatible<CellCustomType>,
    keyCode: number,
    ctrl: boolean,
    shift: boolean,
    alt: boolean,
  ): { cell: Compatible<CellCustomType>; enableEditMode: boolean } {
    const char = getCharFromKeyCode(keyCode, shift);
    const isIME = keyCode === 229 || keyCode === 299;
    if (cell.nonEditable) {
      return {
        cell,
        enableEditMode: false,
      };
    }
    if (isIME) {
      cell.onChange?.();
      return {
        cell,
        enableEditMode: true,
      };
    }
    if (
      ctrl ||
      alt ||
      !isAlphaNumericKey(keyCode) ||
      (shift && keyCode === keyCodes.SPACE)
    ) {
      return {
        cell,
        enableEditMode:
          keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER,
      };
    }
    cell.onChange?.();

    return {
      cell: {
        ...cell,
        text: cell.text + (shift ? char : char.toLowerCase()),
      },
      enableEditMode: true,
    };
  }

  render(
    cell: Compatible<CellCustomType>,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<CellCustomType>, commit: boolean) => void,
  ): ReactNode {
    if (!isInEditMode) {
      return cell.isInValid ? (
        cell.text
      ) : (
        <Styles onKeyDown={(e) => e.stopPropagation()}>
          {cell.text && (
            <Popover
              trigger="click"
              content={
                <a href={cell.text} target="_blank" rel="noreferrer">
                  {cell.text}
                </a>
              }
              overlayStyle={{ zIndex: 2000 }}
            >
              <div>
                <a
                  href={cell.text}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    cursor: "pointer",
                    display: "inline-block",
                    width: "100%",
                    wordBreak: "break-word",
                  }}
                >
                  {cell.text}
                </a>
              </div>
            </Popover>
          )}
        </Styles>
      );
    }

    return (
      <Styles>
        <Input
          style={{
            height: (cell?.rowHeight && cell?.rowHeight - 2) || 46,
          }}
          defaultValue={cell.text}
          ref={(input) => {
            setTimeout(() => {
              input?.focus();
            }, 0);
          }}
          onChange={(e) => {
            const value = e.target.value;
            const updatedCell = this.getCompatibleCell({
              ...cell,
              text: value,
            });
            onCellChanged(updatedCell, false);
          }}
          onBlur={(e) => {
            const value = e.target.value;
            const updatedCell = this.getCompatibleCell({
              ...cell,
              text: value,
            });
            onCellChanged(updatedCell, true);
          }}
          onCopy={(e) => e.stopPropagation()}
          onCut={(e) => e.stopPropagation()}
          onPaste={(e) => {
            e.stopPropagation();
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (isAlphaNumericKey(e.keyCode) || isNavigationKey(e.keyCode))
              e.stopPropagation();
          }}
        />
      </Styles>
    );
  }
}
